import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon.tsx';

// Basiscomponenten: knoppen, chips, velden, kaarten, lege toestanden. Klein en consistent.

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'green' | 'dark';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-deep border border-transparent',
  secondary: 'bg-surface text-ink border border-line-strong hover:bg-tint',
  ghost: 'bg-transparent text-ink-soft hover:bg-tint border border-transparent',
  danger: 'bg-surface text-red border border-red/40 hover:bg-red-soft',
  green: 'bg-green text-white hover:brightness-95 border border-transparent',
  dark: 'bg-bar-2 text-white hover:bg-[#144088] border border-transparent',
};

/** Op aanraakschermen (`pointer-coarse:`) altijd 44 px hoog, ook sm en md; met een muis blijft het compact.
 * Een knop met alleen een icoon is dan ook 44 px breed. */
export function Button(props: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant | undefined; size?: 'sm' | 'md' | 'lg' | undefined; icon?: IconName | undefined; kbd?: string | undefined; busy?: boolean | undefined }) {
  const { variant = 'secondary', size = 'md', icon, kbd, busy, className, children, disabled, ...rest } = props;
  const sizes = { sm: 'h-7 px-2.5 text-[13px] gap-1.5 pointer-coarse:h-11', md: 'h-9 px-3.5 text-sm gap-2 pointer-coarse:h-11', lg: 'h-11 px-5 text-[15px] gap-2' };
  const iconOnly = !children && Boolean(icon || busy);
  return (
    <button type="button" className={`inline-flex items-center justify-center rounded-md font-medium whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-default ${VARIANTS[variant]} ${sizes[size]} ${iconOnly ? 'pointer-coarse:min-w-11' : ''} ${className ?? ''}`} disabled={disabled || busy} {...rest}>
      {busy ? <Icon name="spinner" size={16} className="animate-spin" /> : icon ? <Icon name={icon} size={16} /> : null}
      {children}
      {kbd ? <kbd className={`ml-1 rounded px-1.5 py-0.5 text-[11px] font-sans font-medium pointer-coarse:hidden ${variant === 'primary' || variant === 'green' || variant === 'dark' ? 'bg-white/20' : 'bg-tint text-ink-soft'}`}>{kbd}</kbd> : null}
    </button>
  );
}

/** 36 px met een muis, 44 px op aanraakschermen (ook als een aanroeper met `className` een kleinere maat kiest). */
export function IconButton(props: ButtonHTMLAttributes<HTMLButtonElement> & { icon: IconName; label: string; active?: boolean | undefined; size?: number | undefined; tone?: 'default' | 'amber' | 'light' | undefined }) {
  const { icon, label, active, size, tone = 'default', className, ...rest } = props;
  const tones = {
    default: active ? 'bg-tint text-ink' : 'text-ink-soft hover:bg-tint hover:text-ink',
    amber: 'bg-amber text-white hover:brightness-110',
    light: active ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white',
  };
  return (
    <button type="button" aria-label={label} title={label} aria-pressed={active} className={`inline-flex items-center justify-center rounded-md h-9 w-9 pointer-coarse:h-11 pointer-coarse:w-11 shrink-0 transition-colors ${tones[tone]} ${className ?? ''}`} {...rest}>
      <Icon name={icon} size={size ?? 18} />
    </button>
  );
}

/** `compact`: smaller tekst en minder ruimte (telefoon), maar wel 36 px hoog om te raken; zonder `compact`
 * is een chip met een muis 32 px en op aanraakschermen 40 px. Het label wikkelt nooit (een pil met
 * twee regels past niet); een rij chips wikkelt zelf, met `flex-wrap` op de rij. */
export function Chip(props: { active?: boolean | undefined; onClick?: (() => void) | undefined; children: ReactNode; count?: number | string | undefined; tone?: 'default' | 'blue' | 'amber' | 'green' | 'red' | 'dark' | undefined; kbd?: string | undefined; title?: string | undefined; className?: string | undefined; compact?: boolean | undefined }) {
  const tones = {
    default: props.active ? 'bg-surface text-ink shadow-sm border-line-strong' : 'text-ink-soft hover:text-ink border-transparent',
    blue: props.active ? 'bg-tint text-accent border-tint-2' : 'text-accent hover:bg-tint border-transparent',
    amber: props.active ? 'bg-amber-soft text-amber border-amber/30' : 'text-amber hover:bg-amber-soft border-transparent',
    green: props.active ? 'bg-green-soft text-green border-green/30' : 'text-green hover:bg-green-soft border-transparent',
    red: props.active ? 'bg-red-soft text-red border-red/30' : 'text-red hover:bg-red-soft border-transparent',
    dark: 'bg-white/10 text-white/90 hover:bg-white/20 border-transparent',
  };
  const size = props.compact ? 'gap-1 h-9 pointer-coarse:h-10 px-2 text-[13px] whitespace-nowrap' : 'gap-1.5 h-8 pointer-coarse:h-10 px-3 text-sm whitespace-nowrap';
  return (
    <button type="button" onClick={props.onClick} title={props.title} aria-pressed={props.active} className={`inline-flex items-center ${size} rounded-full font-medium border transition-colors ${tones[props.tone ?? 'default']} ${props.className ?? ''}`}>
      {props.children}
      {props.count !== undefined ? <span className={`${props.compact ? 'text-[12px]' : 'text-[13px]'} ${props.active && (props.tone ?? 'default') === 'default' ? 'text-accent' : 'opacity-70'}`}>{props.count}</span> : null}
      {props.kbd ? <span className="text-[11px] opacity-60 pointer-coarse:hidden">{props.kbd}</span> : null}
    </button>
  );
}

export function Badge(props: { tone: 'blue' | 'amber' | 'green' | 'red' | 'grey'; children: ReactNode; title?: string | undefined }) {
  const tones = { blue: 'bg-accent text-white', amber: 'bg-amber text-white', green: 'bg-green text-white', red: 'bg-red text-white', grey: 'bg-tint-2 text-ink' };
  return (
    <span title={props.title} className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[12px] font-semibold ${tones[props.tone]}`}>
      {props.children}
    </span>
  );
}

export function Field(props: { label: string; children: ReactNode; hint?: string | undefined; className?: string | undefined }) {
  return (
    <label className={`block ${props.className ?? ''}`}>
      <span className="block text-[13px] font-medium text-ink-soft mb-1">{props.label}</span>
      {props.children}
      {props.hint ? <span className="block text-[12px] text-muted mt-1">{props.hint}</span> : null}
    </label>
  );
}

/** Enkelregelige velden en keuzelijsten: 36 px met een muis, 44 px op aanraakschermen. Een aanroeper die
 * een eigen hoogte kiest (bijvoorbeeld `h-24` voor een tekstvak) gebruikt `textareaClass`: `pointer-coarse:h-11`
 * wint op aanraakschermen van elke `h-*` die erbij staat. */
export const inputClass = 'w-full h-9 pointer-coarse:h-11 px-3 rounded-md border border-line-strong bg-surface text-ink placeholder:text-muted focus:border-accent focus:outline-none';
export const textareaClass = 'w-full px-3 py-2 rounded-md border border-line-strong bg-surface text-ink placeholder:text-muted focus:border-accent focus:outline-none resize-y leading-relaxed';

export function Empty(props: { title: string; text?: string | undefined; icon?: IconName | undefined; action?: ReactNode | undefined }) {
  return (
    <div className="h-full flex items-center justify-center p-8 text-center">
      <div className="max-w-sm">
        {props.icon ? <Icon name={props.icon} size={28} className="mx-auto mb-3 text-muted" /> : null}
        <p className="font-display text-xl text-ink mb-1">{props.title}</p>
        {props.text ? <p className="text-muted">{props.text}</p> : null}
        {props.action ? <div className="mt-4">{props.action}</div> : null}
      </div>
    </div>
  );
}

export function Spinner(props: { label?: string | undefined; className?: string | undefined }) {
  return (
    <div className={`flex items-center gap-2 text-muted ${props.className ?? ''}`} role="status">
      <Icon name="spinner" size={16} className="animate-spin" />
      <span>{props.label ?? 'Laden…'}</span>
    </div>
  );
}

/** Sneltoets-hint; op aanraakschermen (zonder toetsenbord) verborgen. */
export function Kbd(props: { children: ReactNode }) {
  return <kbd className="inline-block rounded border border-line-strong bg-surface px-1.5 text-[11px] font-sans text-ink-soft leading-5 pointer-coarse:hidden">{props.children}</kbd>;
}

/** De stip bij een gesprek met een antwoord dat de eigenaar nog niet zag (ticket 30961, deel 2): op zijn tabblad en bij zijn rij in
 * de gesprekkenlijst. */
export function UnreadDot() {
  return <span role="img" aria-label="Nieuw antwoord" title="Nieuw antwoord" data-testid="ongelezen" className="h-2 w-2 shrink-0 rounded-full bg-accent" />;
}

export function Strip(props: { tone: 'blue' | 'amber' | 'green' | 'red' | 'grey' | 'none' }) {
  const tones = { blue: 'bg-accent', amber: 'bg-amber', green: 'bg-green', red: 'bg-red', grey: 'bg-line-strong', none: 'bg-transparent' };
  return <span className={`absolute left-0 top-0 bottom-0 w-1 ${tones[props.tone]}`} aria-hidden="true" />;
}
